import { Input } from '@/components/ui/input'
import { currencyFormat } from '@/utils/currency-format';
import { createFileRoute, useRouter } from '@tanstack/react-router'
import Select, { type GroupBase, type OptionProps, type SingleValueProps, components } from 'react-select'
import { withMask } from 'use-mask-input';

import styles from './styles.module.scss'
import { ModalSuccessWithdraw } from '@/components/ui/modal-success-withdraw';
import { WithdrawStatusMessage } from '@/components/ui/withdraw-status-message';
import { useBoolean, useWithdrawStatus } from '@/hooks';
import { Suspense, useCallback, useState } from 'react';
import { transactionsServiceInstance } from '@/services/transaction.service';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { useAtom } from 'jotai';
import { userAtom } from '@/store/atoms/user.atom';
import { Info } from 'lucide-react';

export const Route = createFileRoute('/mobile/__base-layout/withdraw/')({
  component: RouteComponent,
})


function Option(props: OptionProps<any, false, GroupBase<any>>) {
  const { children, ...rest } = props
  const { data } = rest
  return (
    <components.Option {...rest}>
      <div className={styles.container_option}>
        <img src={data.img} alt={data.label} />
        {children}
      </div>
    </components.Option>
  )
}

function SingleValue(props: SingleValueProps<any, false, GroupBase<any>>) {
  const { children, ...rest } = props
  const { data } = rest
  return (
    <components.SingleValue {...rest}>
      <div className={styles.container_option}>
        <img src={data.img} alt={data.label} />
        {children}
      </div>
    </components.SingleValue>
  )
}

const options = [
  {
    value: 'CPF',
    label: 'CPF',
    img: '/assets/document-text-20-solid.svg'
  },
  {
    value: 'E-mail',
    label: 'E-mail',
    img: '/assets/envelope-solid.svg'
  },
  {
    value: 'Celular',
    label: 'Celular',
    img: '/assets/device-phone-mobile.svg'
  },
  {
    value: 'Chave aleatória',
    label: 'Chave aleatória',
    img: '/assets/key-solid.svg'
  }
]
const masks = {
  'CPF': '999.999.999-99',
  'Celular': '(99) 99999-9999',
}

const formValidation = z.object({
  key: z.string().min(1, 'Chave Pix obrigatória')
})

type FormInput = z.infer<typeof formValidation>

function RouteComponent() {
  const [isOpen, handleOpen, handleClose] = useBoolean()

  const router = useRouter()
  const [user, setUser] = useAtom(userAtom)
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
    img: string;
  } | undefined>(user?.cpf ? options.find(option => option.value === 'CPF') : undefined)
  const { control, handleSubmit } = useForm<FormInput>({
    defaultValues: {
      key: user?.cpf ?? ''
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const { status, pollAfterWithdraw, setStatus } = useWithdrawStatus()

  const isBlocked = status?.blocked ?? false

  const onSubmit = useCallback(async (data: FormInput) => {
    try {
      setIsLoading(true)
      let key = ""
      if (selectedOption?.value === "Celular") {
        const theKey = data.key.replace(/\D/g, '')
        key = `+55${theKey}`
      }
      if (selectedOption?.value === "CPF") {
        key = data.key.replace(/\D/g, '')
      }
      if (selectedOption?.value === "E-mail") {
        key = data.key
      }
      await Promise.all([
        transactionsServiceInstance.send({ key }),
        router.invalidate()
      ])
      // 204: o PIX roda em fila — faz polling do status para capturar falha assíncrona.
      pollAfterWithdraw()
      handleOpen()
      setUser({
        ...user as any,
        wallet: {
          amount: 0
        }
      })
    } catch (err) {
      console.error(err)
      // 400 (ou falha reportada): exibe a mensagem exata vinda do backend.
      if (isAxiosError(err) && err.response?.data?.message) {
        const message = err.response.data.message as string
        setStatus(prev => ({
          blocked: prev?.blocked ?? false,
          blockedUntil: prev?.blockedUntil ?? null,
          message,
        }))
        toast.error(message)
        return
      }
      toast.error("Não foi possível realizar o saque")
    } finally {
      setIsLoading(false)
    }
  }, [selectedOption, user, pollAfterWithdraw, setStatus])


  return (
    <div className={styles.container}>
      <header>
        <h3>Saque</h3>
        <div>
          <Info size={16} />
          <span>A chave Pix precisa pertencer ao usuário cadastrado.</span>
        </div>
      </header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <WithdrawStatusMessage message={status?.message ?? null} blocked={isBlocked} />
          <div>
            <label>Tipo de chave pix</label>
            <Select
              options={options}
              components={{ Option, SingleValue }}
              onChange={newValue => setSelectedOption(newValue as any)}
              value={selectedOption}
            />
          </div>
          {
            !!selectedOption?.value && !!masks[selectedOption?.value as keyof typeof masks] ? (
              <Controller
                control={control}
                name="key"
                render={({ field }) => (
                  <Input
                    key="masked"
                    label='Chave Pix'
                    placeholder='Chave Pix'
                    {...field}
                    //@ts-ignore
                    ref={withMask(masks[selectedOption?.value])}
                  />
                )}
              />

            ) : (
              <Controller
                control={control}
                name="key"
                render={({ field }) => (
                  <Input
                    key="email"
                    label='Chave Pix'
                    placeholder='Chave Pix'
                    type="email"
                    {...field}
                  />
                )}
              />
            )
          }

          <Input
            label='Valor do saque:'
            value={currencyFormat(user?.wallet?.amount || 0)}
            disabled
          />
        </div>
        <footer>
          <button
            disabled={isLoading || isBlocked || (!!user?.wallet && user?.wallet?.amount < 10)}
          >
            <img
              src="/assets/withdraw.png"
            />
          </button>
        </footer>
      </form>
      {
        isOpen && (
          <Suspense>
            <ModalSuccessWithdraw
              isOpen={isOpen}
              onClose={handleClose}
            />
          </Suspense>
        )
      }
    </div>
  )
}
